import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isAccountSynchronizationDone, type StepState } from '../contexts/ActivationChecklistContext';

export function isTrialDemarrageExemptPath(
  pathname: string,
  search: string,
  options?: { isDemoLocked?: boolean },
): boolean {
  if (pathname === '/demarrage') return true;
  if (
    !options?.isDemoLocked
    && pathname === '/ia-analysis'
    && new URLSearchParams(search).get('onboardingDemo') === 'true'
  ) {
    return true;
  }
  return false;
}

/** Condition métier brute : trial actif sans compte synchronisé. Indépendante du device. */
export function trialNeedsAccountConnection(
  hasActiveTrialOnboarding: boolean,
  synchronisation: StepState,
): boolean {
  return hasActiveTrialOnboarding && synchronisation !== 'done';
}

/**
 * Idem, mais désactivée sur mobile : la plupart des users mobile n'ont pas leurs
 * identifiants de broker ni de fichier d'export sous la main. Bloquer leur
 * exploration (mur démo + redirection forcée vers /demarrage) les frustre et les
 * fait quitter l'app — on les laisse naviguer librement, sans mur ni redirection.
 */
export function shouldForceTrialDemarrage(
  hasActiveTrialOnboarding: boolean,
  synchronisation: StepState,
  isMobile: boolean,
): boolean {
  if (isMobile) return false;
  return trialNeedsAccountConnection(hasActiveTrialOnboarding, synchronisation);
}

export async function trialUserNeedsDemarrageLanding(userId: string): Promise<boolean> {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return false;

  const userData = userSnap.data() as Record<string, unknown>;
  const isTrial = userData.isTrial === true || userData.isTrial === 'true';
  const isPremium = userData.isPremium === true || userData.isPremium === 'true';
  const isHomePromo = userData.homePromo === true || userData.homePromo === 'true';
  const isTrialExpired = isTrial && !isPremium;
  // Abonné payant (isPremium sans isTrial) : le parcours d'onboarding trial est terminé,
  // même si homePromo reste à true sur le doc user. Cf. AuthContext.hasActiveTrialOnboarding.
  const hasPaidSubscription = isPremium && !isTrial;
  const hasActiveTrialOnboarding = (isTrial || isHomePromo) && !isTrialExpired && !hasPaidSubscription;

  if (!hasActiveTrialOnboarding) return false;

  const accountsSnap = await getDocs(
    query(collection(db, 'accounts'), where('userId', '==', userId)),
  );

  if (accountsSnap.empty) return true;

  const hasSyncedAccount = accountsSnap.docs.some((accountDoc) =>
    isAccountSynchronizationDone(accountDoc.data() as Record<string, unknown>),
  );

  return !hasSyncedAccount;
}

export async function resolvePostLoginLandingPage(
  userId: string,
  preferredLandingPage = '/dashboard',
): Promise<string> {
  if (await trialUserNeedsDemarrageLanding(userId)) {
    return '/demarrage';
  }
  return preferredLandingPage;
}
